import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'LuxKit/Typography/BodyText'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { EmptyArray } from 'lib/array/arrayUtils'
import React, { useMemo } from 'react'
import { takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import { getFlightDealIdsByYearMonth, markCheapestPrice } from 'selectors/offerPage/offerPageSelectors'
import { getPlural } from 'lib/string/pluralize'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Group from 'components/utils/Group'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import SolidCircleIcon from 'LuxKit/Icons/solid/SolidCircleIcon'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { EmptyObject } from 'lib/object/objectUtils'
import LineFireIcon from 'LuxKit/Icons/line/LineFireIcon'
import { INCLUSIONS_TITLE_BONUS } from 'constants/inclusions'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import { FormattedMessage, useIntl } from 'react-intl'

interface Props {
  fromPrice: number
  duration: number
  withFlights?: boolean
  rooms: Array<App.RoomOccupants>
  year?: string
  month?: string
  flightDealIdsByYearMonth?: Record<string, Record<string, string>>
  displayMemberPrice: boolean
  cheapest?: boolean
  markCheapestPrice: boolean
  hasLoyaltyRoomUpgrade?: boolean
  hasTactical?: boolean
  saleUnit: string
}

function OfferCalendarPriceBlurb(props: Props) {
  const {
    fromPrice,
    duration,
    withFlights,
    flightDealIdsByYearMonth = EmptyObject,
    year,
    month,
    rooms = EmptyArray,
    displayMemberPrice,
    cheapest,
    markCheapestPrice,
    hasLoyaltyRoomUpgrade,
    hasTactical,
    saleUnit,
  }: Props = props

  const averagePrice = Math.ceil(fromPrice / (rooms.length > 1 ? rooms.length : duration))

  const flightDealName = useMemo(() => {
    if (year && year in flightDealIdsByYearMonth) {
      const yearRecord = flightDealIdsByYearMonth[year]

      if (month) {
        if (month in yearRecord) {
          return yearRecord[month]
        }

        return undefined
      }

      return yearRecord[Object.keys(yearRecord)[0]]
    }

    return undefined
  }, [flightDealIdsByYearMonth, year, month])
  const unit = rooms.length > 1 ? saleUnit : 'night'
  const roomCount = rooms.length
  const priceColour = markCheapestPrice && cheapest ? 'highlight-secondary' : 'neutral-three'

  const intl = useIntl()
  const [tier] = useCurrentLoyaltyTier()
  const canLoyaltyUpgrade = useUserHasLoyaltyBenefit('hotel_upgrade')

  const limitedTimeSpecialLabel = intl.formatMessage({
    id: 'J2WhKfm+O5',
    defaultMessage: 'Limited Time Special',
    description: 'Label for limited time special promotion in calendar price blurb',
  })
  const freeUpgradeLabel = intl.formatMessage({
    id: 'alK/hdylD5',
    defaultMessage: 'Free upgrade available',
    description: 'Label indicating a free loyalty upgrade is available in calendar price blurb',
  })

  const priceElement = <BodyText variant="small" weight="bold" colour={priceColour} as="span"><FormatCurrency value={fromPrice} /></BodyText>
  const priceWithAsterisk = <BodyText variant="small" weight="bold" colour={priceColour} as="span"><FormatCurrency value={fromPrice} />*</BodyText>

  let priceBlurb: React.ReactNode
  if (roomCount > 1) {
    priceBlurb = <FormattedMessage
      id="Yuh4xYLygb"
      defaultMessage="{roomCount} {saleUnitLabel}, {nights, plural, one {# night} other {# nights}} from {price} total"
      description="Price blurb showing room count, sale unit, nights, and total price in calendar (multi-room)"
      values={{
        roomCount,
        saleUnitLabel: getPlural(saleUnit, roomCount),
        nights: duration,
        price: priceElement,
      }}
    />
  } else {
    priceBlurb = <FormattedMessage
      id="8qf+Y3xjUR"
      defaultMessage="{nights, plural, one {# night} other {# nights}} from {price} total"
      description="Price blurb showing nights and total price in calendar (single room)"
      values={{
        nights: duration,
        price: priceElement,
      }}
    />
  }

  let priceBlurbWithFlights: React.ReactNode
  if (roomCount > 1) {
    priceBlurbWithFlights = <FormattedMessage
      id="2LePnKE1Aq"
      defaultMessage="{roomCount} {saleUnitLabel}, {nights, plural, one {# night} other {# nights}} + round trip flights from {price} (2 adults)"
      description="Price blurb showing room count, sale unit, nights, flights, and total price in calendar (multi-room)"
      values={{
        roomCount,
        saleUnitLabel: getPlural(saleUnit, roomCount),
        nights: duration,
        price: priceWithAsterisk,
      }}
    />
  } else {
    priceBlurbWithFlights = <FormattedMessage
      id="MNUWFG8hmu"
      defaultMessage="{nights, plural, one {# night} other {# nights}} + round trip flights from {price} (2 adults)"
      description="Price blurb showing nights, flights, and total price in calendar (single room)"
      values={{
        nights: duration,
        price: priceWithAsterisk,
      }}
    />
  }

  let FireIcon
  if (isLPCTacticalEnabled()) {
    FireIcon = <CaptionTextBlock startIcon={<SolidFireIcon colour="urgency" />} variant="medium">
      {limitedTimeSpecialLabel}
    </CaptionTextBlock>
  } else {
    FireIcon = <CaptionTextBlock startIcon={<LineFireIcon colour="highlight-secondary" />} variant="medium" format="titlecase">
      {INCLUSIONS_TITLE_BONUS} Available
    </CaptionTextBlock>
  }

  return (
    <Group
      direction="vertical"
      gap={4}
      tabletDirection="horizontal"
      tabletHorizontalAlign="space-between"
      fullWidth
    >
      {!withFlights && <BodyText variant="small" colour="neutral-one">
        {displayMemberPrice &&
          <BodyText weight="bold" colour="lux-plus" variant="small" as="span" style={{ marginInlineEnd: '0.25em' }}>
            {intl.formatMessage(
              {
                id: 'KrcdtbVR1w',
                defaultMessage: 'Showing {programName} member prices',
                description: 'Label indicating LuxPlus member prices are displayed in calendar price blurb',
              },
              { programName: LUXURY_PLUS.PROGRAM_NAME },
            )}
          </BodyText>}
        {priceBlurb}
        <CSSBreakpoint max="mobile" as="br" />
        <BodyText variant="small" colour="neutral-three" as="span" style={{ marginInlineStart: '0.25em' }}>
          <FormattedMessage
            id="jVrWgVwb75"
            defaultMessage="Average {price}/{unit}"
            description="Average price per unit in calendar price blurb"
            values={{ price: <FormatCurrency value={averagePrice} />, unit }}
          />
        </BodyText>
      </BodyText>}
      {withFlights && <BodyText variant="small" colour="neutral-one">
        {displayMemberPrice &&
          <BodyText weight="bold" colour="lux-plus" variant="small" as="span" style={{ marginInlineEnd: '0.25em' }}>
            {intl.formatMessage(
              {
                id: 'LMktmCIqvM',
                defaultMessage: 'Showing {programName} member prices',
                description: 'Label indicating LuxPlus member prices are displayed in calendar price blurb with flights',
              },
              { programName: LUXURY_PLUS.PROGRAM_NAME },
            )}
          </BodyText>}
        {priceBlurbWithFlights}
        <CSSBreakpoint max="mobile" as="br" />
        <BodyText variant="small" colour="neutral-three" as="span" style={{ marginInlineStart: '0.25em' }}>
          <FormattedMessage
            id="IoBwYrHYt8"
            defaultMessage="Average {price}/{unit}"
            description="Average price per unit in calendar price blurb with flights"
            values={{ price: <FormatCurrency value={averagePrice} />, unit }}
          />
        </BodyText>
      </BodyText>}
      <Group direction="horizontal" gap={24}>
        {flightDealName && <CaptionTextBlock
          variant="medium"
          colour="neutral-two"
          startIcon={<SolidFireIcon colour="urgency" />}
        >
          {flightDealName}
        </CaptionTextBlock>}
        {hasTactical && FireIcon}
        {hasLoyaltyRoomUpgrade && canLoyaltyUpgrade && <CaptionTextBlock
          variant="medium"
          colour="neutral-two"
          startIcon={<SolidCircleIcon colour={`tier-${tier}`} />}
        >
          {freeUpgradeLabel}
        </CaptionTextBlock>}
      </Group>
    </Group>
  )
}

export default withOfferPageAndReduxState((currentState, appState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    duration: state.duration,
    withFlights: state.flights,
    rooms: state.rooms,
    flightDealIdsByYearMonth: getFlightDealIdsByYearMonth(appState, state).dealIds,
    displayMemberPrice: state.displayMemberPrice,
    markCheapestPrice: markCheapestPrice(state),
    saleUnit: state.offer?.saleUnit ?? 'room',
  }
}, OfferCalendarPriceBlurb)
